import axios from 'axios';
import _ from 'lodash';
import qs from 'qs';

import config from '../config';

const baseURL = () => {
	const apiUrl = config.REACT_APP_API_URL || "http://localhost:4000/";//"https://geo-api-staging.landmarksid.com/";// 
	return apiUrl;
};

const getInstance = ()=>{
	const token = localStorage.getItem('token');
	return axios.create({
		headers: {'Authorization': `Bearer ${token}`},
		baseURL: baseURL(),
	});
}

const handleResponse = (response)=> {
	if (response.status >= 200 && response.status < 300){
		return { error: null, json: response.data, statusCode: response.status, message: response.data.message};
	}
}

const handleError = (errorObject) => {
	return { 
		error: errorObject.response.status, 
		json: errorObject.response.data.body, 
		statusCode: errorObject.response.status,
		message: errorObject.response.data.message,
	};
}

const LandmarksAPI = {
	signIn: (credentials) => {
		let url = `${baseURL()}auth/signin`;
		return axios.post(url, credentials).then(handleResponse).catch(handleError);
	},
	getApps: () => {
		let url = `${baseURL()}api/apps`;
		return getInstance().get(url).then(handleResponse).catch(handleError);
	},
	getAppOrganizations: (appId) => {
		let url = `${baseURL()}api/apps/${appId}/organizations`;
		return getInstance().get(url).then(handleResponse).catch(handleError);
	},
	createApp: (app) => {
		let url = `${baseURL()}api/apps`;
		return getInstance().post(url, app).then(handleResponse).catch(handleError);
	},
	deleteApp: (appId) => {
		let url = `${baseURL()}api/apps/${appId}`;
		return getInstance().delete(url).then(handleResponse).catch(handleError);
	},
	// App Organization Mapping
	removeOrganizationFromApp: (appId, organizationId) => {
		let url = `${baseURL()}api/apps/${appId}/organizations/${organizationId}`;
		return getInstance().delete(url).then(handleResponse).catch(handleError);
	},
	addOrganizationToApp: (appId, organizationId) => {
		let url = `${baseURL()}api/apps/${appId}/organizations`;
		return getInstance().post(url, { organizationId })
					.then(handleResponse).catch(handleError);
	},
	getPOISummary: () => {
		let url = `${baseURL()}api/organizations/summary`;
		return getInstance().get(url).then(handleResponse).catch(handleError);
	},
	getCategories: () => {
		let url = `${baseURL()}data/categories`;
		return getInstance().get(url).then(handleResponse).catch(handleError);
	},
	getOrganizations: () => {
		let url = `${baseURL()}api/organizations`;
		return getInstance().get(url).then(handleResponse).catch(handleError);
	},
	getOrganization: (organizationId) => {
		let url = `${baseURL()}api/organizations/${organizationId}`;
		return getInstance().get(url).then(handleResponse).catch(handleError);
	},
	createOrganization: (organization) => {
		let url = `${baseURL()}api/organizations`;
		return getInstance().post(url, organization)
					.then(handleResponse).catch(handleError);
	},
	removeOrganization: (organizationId) => {
		let url = `${baseURL()}api/organizations/${organizationId}`;
		return getInstance().delete(url).then(handleResponse).catch(handleError);
	},
	createOrganizationFromPlaces: (organization) => {
		let url = `${baseURL()}api/pois/import`;
		return getInstance().post(url, organization)
					.then(handleResponse).catch(handleError);
	},
	getOrganizationPois: (organizationId, searchTerm, page = 0) => {
		let url = `${baseURL()}api/organizations/${organizationId}/pois`;
		const queryURL = qs.stringify({ searchQuery: searchTerm, pageNumber: page });
		return getInstance().get(`${url}?${queryURL}`)
					.then(handleResponse).catch(handleError); 
	},
	removePOI: (poiId) => {
		let url = `${baseURL()}api/pois/${poiId}`;
		return getInstance().delete(url)
					.then(handleResponse).catch(handleError);
	},
	markPOIUnsure: (poiId) => {
		let url = `${baseURL()}api/pois/${poiId}/unsure`;
		return getInstance().post(url)
					.then(handleResponse).catch(handleError);
	},
	getMapFences: (mapFilters) => {
		let url = `${baseURL()}api/fences/search`;
		return getInstance().post(url, mapFilters)
					.then(handleResponse).catch(handleError);
	},
	loadPOIMappings: () => {
		let url = `${baseURL()}api/landparcels`;
		return getInstance().get(url)
					.then(handleResponse).catch(handleError);
	},
	loadPOIMappingsForLandparcel: (fenceId) => {
		let url = `${baseURL()}api/landparcels/${fenceId}`;
		return getInstance().get(url)
					.then(handleResponse).catch(handleError);
	},
	mapLandparcel: (landparcelId, POIIds) => {
		let url = `${baseURL()}api/landparcels/mapped`;
		return getInstance().post(url, {landparcelId, POIIds})
					.then(handleResponse).catch(handleError);
	},
	removeFences: (fenceIds) => {
		let url = `${baseURL()}api/fences/remove`;
		return getInstance().post(url, {fenceIds})
					.then(handleResponse).catch(handleError);
	},
	getPoi: (poiId) => {
		let url = `${baseURL()}api/pois/${poiId}`;
		return getInstance().get(url)
					.then(handleResponse).catch(handleError);
	},
	patchPOI: (poiId, patchBody) => {
		let url = `${baseURL()}api/pois/${poiId}`;
		return getInstance().patch(url, patchBody)
					.then(handleResponse).catch(handleError);
	},
	createPoiFence: (poiId, fence, accuracy) => {
		let url = `${baseURL()}api/pois/${poiId}/fences`;
		return getInstance().post(url, { fence: { geo: fence }, accuracy } )
					.then(handleResponse).catch(handleError);
	},
	importAddresses: (organizationId, addresses) => {
		let url = `${baseURL()}api/organizations/${organizationId}/addresses`;
		return getInstance().post(url, { addresses })
					.then(handleResponse).catch(handleError);
	},
	importPOIs: (organizationId, pois) => {
		let url = `${baseURL()}api/organizations/${organizationId}/pois/import`;
		return getInstance().post(url, pois)
					.then(handleResponse).catch(handleError);
	},
	getPOIDuplicates: (poiId) => {
		let url = `${baseURL()}api/pois/${poiId}/duplicates`;
		return getInstance().get(url)
					.then(handleResponse).catch(handleError);
	},
	getFencesToBeValidated: (limit, searchTerm, country) => {
		let url = `${baseURL()}api/fences/validate`;
		if (typeof limit !== 'undefined') {
			url += `?limit=${limit}`;
			if (!_.isUndefined(searchTerm)) url += `&searchTerm=${searchTerm}`;
			if (!_.isUndefined(country)) url += `&country=${country}`;
		}
		return getInstance().get(url)
					.then(handleResponse).catch(handleError);
	},
	getFence: (fenceId) => {
		let url = `${baseURL()}api/fences/${fenceId}`;
		return getInstance().get(url)
					.then(handleResponse).catch(handleError);
	},
	putFence: (fenceId, geo, accuracy) => {
		let url = `${baseURL()}api/fences/${fenceId}`;
		return getInstance().put(url, { geo, accuracy })
					.then(handleResponse).catch(handleError);
	},
	validateFence: (fenceId) => {
		let url = `${baseURL()}api/fences/${fenceId}/validate`;
		return getInstance().put(url)
					.then(handleResponse).catch(handleError);
	},
	skipFence: (fenceId) => {
		let url = `${baseURL()}api/fences/${fenceId}/skip`;
		return getInstance().put(url)
					.then(handleResponse).catch(handleError);
	},
	setFencePrimaryPoi: (fenceId, poiId) => {
		let url = `${baseURL()}api/fences/${fenceId}/primary`;
		return getInstance().put(url, { poiId })
				.then(handleResponse).catch(handleError);
	},
	// POI validation
	markValidationStageComplete: (poiId, stage) => {
		let url = `${baseURL()}api/validation/${poiId}/complete`;
		return getInstance().post(url, { stage })
				.then(handleResponse).catch(handleError);
	},
	
	// Jobs

	getNextJob: (limit) => {
		const queryURL = qs.stringify({ limit: limit });
		let url = `${baseURL()}api/jobs?${queryURL}`;
		return getInstance().get(url).then(handleResponse).catch(handleError);
	},
	getPOIsToBeValidated: (organizationId, task) => {
		const queryURL = qs.stringify( { task });
		let url = `${baseURL()}api/organizations/${organizationId}/pois/toBeValidated?${queryURL}`;
		return getInstance().get(url).then(handleResponse).catch(handleError);
	},
	getJobs: () => {
		let url = `${baseURL()}admin/jobs`;
		return getInstance().get(url).then(handleResponse).catch(handleError);
	},
	getJobsSummary: (filters) => {
		const query = qs.stringify(filters);
		let url = `${baseURL()}admin/jobs/summary?${query}`;
		return getInstance().get(url).then(handleResponse).catch(handleError);
	},
	patchJob: (jobId, patchBody) => {
		let url =`${baseURL()}admin/jobs/${jobId}`;
		return getInstance().patch(url, patchBody).then(handleResponse).catch(handleError);
	},

	// Account Functions
	getAccounts: () => {
		let url = `${baseURL()}admin/accounts`;
		return getInstance().get(url).then(handleResponse).catch(handleError);
	},
	createAccount: (account) => {
		let url =`${baseURL()}admin/createAccount`;
		return getInstance().post(url, account).then(handleResponse).catch(handleError);
	},

	// Validation statistics
	getValidationStatistics: () => {
		let url = `${baseURL()}admin/validation/statistics`;
		return getInstance().get(url).then(handleResponse).catch(handleError);
	}
}

export default LandmarksAPI;


// WEBPACK FOOTER //
// ./src/api/landmarksid.js